import { BadgeDefinitionAPI, BadgeLevelAPI, StudentBadgeProgressAPI, BadgeXpEventAPI } from '../services/badgeService';
import { BadgeDefinition, Badge, Activity, StudentBadgeProgress, BadgeLevel } from '../types/badges';

export const transformBadgeDefinitionFromAPI = (apiBadge: BadgeDefinitionAPI): BadgeDefinition => {
  const sortedLevels = apiBadge.badgeLevels.sort(
    (a, b) => a.levelPointsRequired - b.levelPointsRequired
  );

  const levelImages: Record<string, string> = {};
  const levelThresholds: Record<string, number> = {};

  sortedLevels.forEach(l => {
    const key = l.levelName.toLowerCase();
    levelImages[key] = l.levelImageUrl || ``;
    levelThresholds[key] = l.levelPointsRequired;
  });

  return {
    id: apiBadge.badgeId,
    name: apiBadge.badgeName,
    slug: apiBadge.badgeSlug ?? apiBadge.badgeName.toLowerCase().replace(/\s+/g, '-'),
    category: apiBadge.badgeName.toLowerCase().replace(/\s+/g, '-'), // Generate category from name
    description: apiBadge.badgeDescription || '',
    badgeImageUrl: apiBadge.badgeImageUrl,
    levelImages,
    levelThresholds,
    color: '#3B82F6' // Default blue color
  };
};

export const transformStudentBadgeProgressFromAPI = (apiProgress: StudentBadgeProgressAPI): Badge => {
  const badgeDefinition = transformBadgeDefinitionFromAPI(apiProgress.badge);

  const thresholds = Object.entries(badgeDefinition.levelThresholds).sort((a, b) => a[1] - b[1]);
  let currentLevel: BadgeLevel | 'none' = 'none';
  for (const [level, threshold] of thresholds) {
    if (apiProgress.currentXp >= threshold) {
      currentLevel = level;
    }
  }

  return {
    id: badgeDefinition.id,
    name: badgeDefinition.name,
    slug: badgeDefinition.slug,
    category: badgeDefinition.category,
    currentLevel,
    currentXP: apiProgress.currentXp,
    description: badgeDefinition.description,
    badgeImageUrl: badgeDefinition.badgeImageUrl,
    levelImages: badgeDefinition.levelImages,
    levelThresholds: badgeDefinition.levelThresholds,
    color: badgeDefinition.color
  };
};

export const transformStudentProgressFromAPI = (apiProgress: StudentBadgeProgressAPI): StudentBadgeProgress => {
  const badgeDefinition = transformBadgeDefinitionFromAPI(apiProgress.badge);
  const thresholds = Object.entries(badgeDefinition.levelThresholds).sort((a, b) => a[1] - b[1]);
  let currentLevel: BadgeLevel | 'none' = 'none';
  for (const [level, threshold] of thresholds) {
    if (apiProgress.currentXp >= threshold) {
      currentLevel = level;
    }
  }

  return {
    badgeId: apiProgress.badgeId,
    badgeName: apiProgress.badge.badgeName,
    currentXP: apiProgress.currentXp,
    // Prefer explicit level from API when present; otherwise derive from thresholds
    levelNumber: apiProgress.currentLevel?.levelNumber ?? thresholds.filter(([, thr]) => apiProgress.currentXp >= thr).length,
    currentLevel,
    badgeImageUrl: badgeDefinition.badgeImageUrl,
    levelImageUrl: currentLevel !== 'none' ? badgeDefinition.levelImages[currentLevel] : undefined
  };
};

export const transformXPHistoryFromAPI = (apiEvents: BadgeXpEventAPI[]): Activity[] => {
  // Guard against non-array inputs to prevent runtime errors
  if (!Array.isArray(apiEvents)) {
    return [];
  }

  return apiEvents.map(event => ({
    id: event.badgeXpEventId.toString(),
    type: 'award' as const,
    amount: event.deltaXp,
    reason: event.reason || 'XP Awarded',
    timestamp: new Date(event.createdAt),
    awardedBy: {
      id: event.createdByUser.id,
      name: `${event.createdByUser.firstName || ''} ${event.createdByUser.lastName || ''}`.trim() || event.createdByUser.email,
      role: 'teacher' // Default role
    }
  }));
};

export const transformBadgeDefinitionToAPI = (badge: Partial<BadgeDefinition>): Omit<BadgeDefinitionAPI, 'badgeId' | 'badgeLevels'> & { badgeLevels: Omit<BadgeLevelAPI, 'badgeLevelId'>[] } => {
  const levels: Omit<BadgeLevelAPI, 'badgeLevelId'>[] = [];

  if (badge.levelThresholds) {
    const entries = Object.entries(badge.levelThresholds).sort((a, b) => a[1] - b[1]);
    entries.forEach(([levelName, threshold], index) => {
      const level: any = {
        levelName: levelName.charAt(0).toUpperCase() + levelName.slice(1),
        levelNumber: index + 1,
        levelPointsRequired: threshold,
        levelImageUrl: badge.levelImages?.[levelName],
        levelDescription: `${levelName.charAt(0).toUpperCase() + levelName.slice(1)} level`
      };
      if (badge.id) {
        level.badgeId = badge.id;
      }
      levels.push(level);
    });
  }

  return {
    badgeName: badge.name || '',
    badgeSlug: badge.slug ?? badge.name?.toLowerCase().replace(/\s+/g, '-') ?? '',
    badgeDescription: badge.description,
    badgeImageUrl: badge.badgeImageUrl ?? (badge.levelImages ? Object.values(badge.levelImages)[0] : undefined),
    badgeLevels: levels
  };
};
